import { CONTENT_TYPE_TO_URL_SEGMENT_MAP } from 'adapters'
import { RouteErrorView } from 'components/App/components/ErrorFallback/RouteErrorView'
import FeatureChecker from 'components/FeatureChecker'
import { AnyContent, ContentItem, Feed, RangeFilter, Stream } from 'interfaces'
import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useLocation, useMatch, useNavigate } from 'react-router'
import { from, of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { userSavedStreamsSelector, userStreamIdsSelector, userStreamsSelector } from 'services/content/selectors'
import { createStream, deleteStream, getStreams } from 'services/content/streams/actions'
import { checkFeatureAvailability, checkProductLimit } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_FIND_MY_STREAMS_SAVE, FEATURE_FIND_PRIVATE_STREAMS, LIMIT_SAVED_STREAMS } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { ScrollConfiguration } from './StreamBricksView'
import { StreamFeedRoute } from './StreamFeedRoute'
import StreamFeedsNavigation from './StreamFeedsNavigation'
import { StreamRoute } from './StreamRoute'
import StreamViewHeader from './StreamViewHeader'
import StreamViewNavigation from './StreamViewNavigation'
import { FilterType } from 'shared'

export interface StreamViewProps {
  stream: Stream
  navigation: StreamViewNavigation
  headerButton?: React.ReactElement<any>
  showBackLink?: boolean
  hideSaveButton?: boolean
  itemWidth?: number
  scroll?: ScrollConfiguration

  // These two methods are used to fix an issue with Redirect not working in SearchStreamRoute.
  renderFeedRedirect?: (pathname: string) => React.ReactNode
  renderStreamRedirect?: (pathname: string) => React.ReactNode

  onBackClick?(): void
  onFeedItemPinned?(stream: Stream, feed: Feed, content: AnyContent): void
  onFeedItemShared?(stream: Stream, feed: Feed, content: AnyContent): void | boolean
  onFeedFilterChanged?(stream: Stream, feed: Feed, filter: FilterType): void
  onFeedRangeChanged?(stream: Stream, feed: Feed, range: RangeFilter): void
  onFeedGotoClicked?(stream: Stream, feed: Feed, content: AnyContent, by: string): void
  onStreamItemPinned?(stream: Stream, content: AnyContent): void
  onStreamItemShared?(stream: Stream, content: AnyContent): void | boolean
  onStreamFilterChanged?(stream: Stream, filter: FilterType): void
  onStreamRangeChanged?(stream: Stream, range: RangeFilter): void
  onStreamGotoClicked?(stream: Stream, content: AnyContent, by: string): void
  onViewFeed?(stream: Stream, feed: Feed): void
  onContentItemClick?(item: ContentItem): void
  onStreamSaved?(stream: Stream): void
  onMount?(stream: Stream): void
}

export function StreamView(props: StreamViewProps) {
  const { onMount, onStreamSaved } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const intl = useIntl()
  const navigate = useNavigate()
  const streams = useSelector(userStreamsSelector)
  const savedStreamsCount = useSelector(userSavedStreamsSelector).length
  const streamIds = useSelector(userStreamIdsSelector)
  const [loading, setLoading] = useState(false)
  const match = useMatch(`${props.navigation.prefix}/feeds/:feedId/*`)

  const activeFeed = props.stream.feeds.find(f => f.id === match?.params.feedId || f.uniqueSource === match?.params.feedId)
  const isMyStreamsView = location.pathname.indexOf('/content/my-streams') === 0

  const feedViewMatch = useMatch(`${props.navigation.prefix}/feeds/:feed/:type/:range`)
  const streamViewMatch = useMatch(`${props.navigation.prefix}/:type/:range`)

  // Get filter and range from URL based on current view
  const currentType = feedViewMatch?.params.type || streamViewMatch?.params.type
  const currentRange = feedViewMatch?.params.range || streamViewMatch?.params.range
  const filterUrl = currentType ? CONTENT_TYPE_TO_URL_SEGMENT_MAP[currentType] : undefined
  const rangeUrl = currentRange
  const activeFeedId = feedViewMatch?.params.feed

  const delete$ = useRef(new Subject<string>())
  const save$ = useRef(new Subject<void>())

  React.useEffect(() => {
    if (onMount) {
      onMount(props.stream)
    }
  }, [props.stream, onMount])

  useEffect(() => {
    const deleteSubscription = delete$.current
      .pipe(
        mergeMap(id => {
          return from(dispatch(deleteStream(id)).unwrap()).pipe(catchError((error: any) => {
            return observableOf({ error })
          }))
        })
      )
      .subscribe((response: any) => {
        setLoading(false)
        if (response.error) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        }
      })

    return () => {
      deleteSubscription.unsubscribe()
    }
  }, [dispatch, intl])

  useEffect(() => {
    const saveSubscription = save$.current
      .pipe(
        mergeMap(() => {
          const id = props.stream.originalId || props.stream.id
          return from(
            dispatch(createStream({ name: props.stream.title, id, color: props.stream.color })).unwrap()
          )
            .pipe(
              mergeMap(() => from(dispatch(getStreams(true)).unwrap())),
              catchError((error) => {
                return observableOf({ error })
              })
            )
        })
      )
      .subscribe((response: any) => {
        setLoading(false)
        if (response.error) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        } else if (onStreamSaved) {
          onStreamSaved(props.stream)
        }
      })

    return () => {
      saveSubscription.unsubscribe()
    }
  }, [dispatch, intl, props.stream, onStreamSaved])

  const isSavedStream = useMemo(() => {
    const stream = props.stream
    const id = stream.id
    const originalId = stream.originalId || stream.id
    const ids = { [originalId]: true, [id]: true }
    return streamIds.indexOf(id) !== -1
      || streamIds.indexOf(originalId) !== -1
      || !!streams.find(s => ids[s.originalId || ''] || ids[s.id])
  }, [props.stream, streamIds, streams])

  const toggleStreamSaved = () => {
    // If it's already saved stream, delete it
    if (isSavedStream) {
      const originalId = props.stream.originalId || props.stream.id
      const userStreamToDelete = streams.find(s => s.id === props.stream.id || s.originalId === originalId)
      if (userStreamToDelete) {
        setLoading(true)
        delete$.current?.next(userStreamToDelete.id)
      }
    } else {
      const saveStreamAllowed = dispatch(checkFeatureAvailability(FEATURE_FIND_MY_STREAMS_SAVE))
      if (!saveStreamAllowed) {
        return
      }

      const withinLimit = dispatch(checkProductLimit(LIMIT_SAVED_STREAMS, savedStreamsCount + 1))
      if (!withinLimit) {
        return
      }

      setLoading(true)
      save$.current?.next()
    }
  }

  const goBack = () => navigate(-1)

  const gotoHome = () => {
    navigate('/content')
  }

  return (
    <section data-test="stream-view">
      {props.stream.isPrivate && (
        <FeatureChecker features={[FEATURE_FIND_PRIVATE_STREAMS]} closeCallback={gotoHome} />
      )}
      <StreamViewHeader
        isSaved={isSavedStream}
        saving={loading}
        stream={props.stream}
        navigation={props.navigation}
        location={{
          search: location.search,
          state: location.state
        }}
        activeFeed={activeFeed}
        hideSaveButton={props.hideSaveButton}
        customButton={props.headerButton}
        showBackLink={props.showBackLink === undefined ? isMyStreamsView : props.showBackLink}
        onSave={toggleStreamSaved}
        onBackClick={props.onBackClick || goBack}
      />
      <StreamFeedsNavigation
        stream={props.stream}
        prefix={props.navigation.prefix}
        search={location.search}
        activeFeedId={activeFeedId}
        linkToUserFeedId={isMyStreamsView}
        filterUrl={filterUrl}
        rangeUrl={rangeUrl}
      />
      <Routes>
        <Route
          path="feeds/:feed/*"
          element={(
            <StreamFeedRoute
              stream={props.stream}
              itemWidth={props.itemWidth}
              isSavedStream={isSavedStream}
              isMyStreamsView={isMyStreamsView}
              navigation={props.navigation}
              streamIds={streamIds}
              hideFeedsNav
              renderFeedRedirect={props.renderFeedRedirect}
              onContentItemClick={props.onContentItemClick}
              onFilterChanged={props.onFeedFilterChanged}
              onRangeChanged={props.onFeedRangeChanged}
              onGotoClicked={props.onFeedGotoClicked}
              onItemPinned={props.onFeedItemPinned}
              onItemShared={props.onFeedItemShared}
              onView={props.onViewFeed}
            />
          )}
          errorElement={<RouteErrorView />}
        />
        <Route
          path="/*"
          element={(
            <StreamRoute
              stream={props.stream}
              navigation={props.navigation}
              scroll={props.scroll}
              itemWidth={props.itemWidth}
              isSavedStream={isSavedStream}
              isMyStreamsView={isMyStreamsView}
              streamIds={streamIds}
              hideFeedsNav
              onContentItemClick={props.onContentItemClick}
              onItemPinned={props.onStreamItemPinned}
              onItemShared={props.onStreamItemShared}
              onFilterChanged={props.onStreamFilterChanged}
              onRangeChanged={props.onStreamRangeChanged}
              onGotoClicked={props.onStreamGotoClicked}
              renderStreamRedirect={props.renderStreamRedirect}
            />
          )}
          errorElement={<RouteErrorView />}
        />
      </Routes>
    </section>
  )
}

export default StreamView
